"use client"
import { useMediaQuery } from "@react-hooks-library/core";
import { getOrgSize } from "src/components/images/imageFunctions";
import theme from "src/components/themes/DefaultTheme";
import { gql } from "graphql-request";
import { urlForAsset } from "src/lib/client/imageUrls";
import { useFilminfo } from "src/lib/client/useFilminfo";
import { Maybe, MovieClip, MovieClipVersion } from "src/lib/movieinfo/movieinfotypes";
import styled from 'src/lib/styles/css';
import IPremiumLandingPage from "src/lib/types/IPremiumLandingPage";
import { useEffect, useMemo, useRef } from "react";

//#region [Props]
type HeroProps = {
    landingpage: IPremiumLandingPage;
};
//#endregion

//#region [Component]
export default function Hero({ landingpage }: HeroProps) {
    const isMobile = useMediaQuery("(max-width: 549px)");
    const videoRef = useRef(null);


    const noVideo = (isMobile && !landingpage.bgVideoIdMobile) || (!isMobile && !landingpage.bgVideoIdDesktop);

    const { fiLoading, fiData } = useFilminfo(BGVIDEO_URLS_QUERY, { clipIdVertical: landingpage.bgVideoIdMobile, clipIdHorizontal: landingpage.bgVideoIdDesktop }, { active: !noVideo });

    const [videoSrc, posterSrc] = useMemo(() => {
        let videoSrc: string | undefined | null;
        let posterSrc: string | undefined;
        if (!fiLoading && fiData) {
            //console.debug("fiData", fiData);
            const clips = fiData.movieQuery as Clips;
            const video = isMobile ? clips.verticalClip?.movieClipVersions : clips.horizontalClip?.movieClipVersions;
            if (video) {
                const findr = (size: string) => { return (v: Maybe<MovieClipVersion>) => v?.name === size };
                videoSrc = video.find(findr("medium"))?.url;
                const width = window?.innerWidth;
                if (width >= 1920) {
                    videoSrc = video.find(findr("1080p"))?.url ?? video.find(findr("hd"))?.url ?? videoSrc;
                } else if (width >= 1000) {
                    videoSrc = video.find(findr("hd"))?.url || videoSrc;
                }

                const posterJsonVert = landingpage.vertStill ? [
                    { mq: "(max-width: 640px) and (orientation: portrait)", img: 640, orientation: "portrait" },
                    { mq: "(min-width: 641px and max-width: 1000px) and (orientation: portrait)", img: 1000, orientation: "portrait" },
                    { mq: "(min-width: 1000px and max-width: 1500px) and (orientation: portrait)", img: 1500, orientation: "portrait" },
                    { mq: "(min-width: 1501px) and (orientation: portrait)", img: 1920, orientation: "portrait" }
                ] : [];

                const posterJsonHoriz = landingpage.horizStill ? [
                    { mq: "(max-width: 640px) and (orientation: landscape)", img: 640, orientation: "landscape" },
                    { mq: "(min-width: 641px and max-width: 1000px) and (orientation: landscape)", img: 1000, orientation: "landscape" },
                    { mq: "(min-width: 1000px and max-width: 1500px) and (orientation: landscape)", img: 1500, orientation: "landscape" },
                    { mq: "(min-width: 1501px) and (orientation: landscape)", img: 1920, orientation: "landscape" }
                ] : [];

                const posterJson = (posterJsonVert
                    .concat(posterJsonHoriz)
                    .find(p => window.matchMedia(p.mq).matches));

                if (posterJson) {
                    const img = posterJson.orientation === "portrait" ? landingpage.vertStill : landingpage.horizStill;
                    const assetRef = img?.asset?._ref;
                    if (assetRef && assetRef.startsWith("image-")) {
                        const size = getOrgSize(assetRef);
                        const w = size.width;
                        const h = size.height;
                        posterSrc = urlForAsset(assetRef)
                            .auto("format")
                            .width(w).height(h)
                            .url();
                    }
                }

                return [videoSrc, posterSrc];
            }
        }
        return [null, null];
    }, [fiLoading, fiData, isMobile, landingpage.horizStill, landingpage.vertStill]);

    return <SHero>
        <SVideoWrapper>
            {!noVideo && !fiLoading && videoSrc && <SVideo ref={videoRef}
                src={videoSrc}
                poster={posterSrc}
                autoPlay muted loop playsInline></SVideo>}
            <SBottomShade $bgColor={landingpage.bgColor.hex}></SBottomShade>
        </SVideoWrapper>
    </SHero>;
}
//#endregion

//#region [Styles]
const SHero = styled.div`
	position: absolute;
	width: 100%;
	z-index: -1;
`;

const SVideoWrapper = styled.div`
	position: relative;
`;

const SVideo = styled.video`
	width: 100%;
`;

type SBottomShadeProps = {
    $bgColor: string;
}

const SBottomShade = styled.div<SBottomShadeProps>`
	min-height: 200px;
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;

	background: linear-gradient(180deg, ${props => `${props.$bgColor}00`} 0%, ${props => `${props.$bgColor}ff`} 100%);
`;
//#endregion

const BGVIDEO_URLS_QUERY = gql`
query ($clipIdVertical: Int!, $clipIdHorizontal: Int!) {
	movieQuery {
		verticalClip: getMovieClip(clipId: $clipIdVertical) {
			movieClipVersions {
				url
				name
			}
		}
    	horizontalClip: getMovieClip(clipId: $clipIdHorizontal) {
			movieClipVersions {
				url
				name
			}
    	}
  	}
}
`;

type Clips = {
    verticalClip: MovieClip;
    horizontalClip: MovieClip;
};